/**
 * QG Chrome — vanilla JS behavior for qg-header, qg-mega-menu, qg-header-drawer
 * and qg-announcement. No framework, no build step. Reuses the theme's global
 * `trapFocus`/`removeTrapFocus` helpers (global.js, loaded before this file).
 *
 * Sections covered:
 *   1. Theme toggle (dark/light) — localStorage persistence, prefers-color-scheme seed
 *   2. Mega menu — hover-open + click/keyboard operable, one open at a time
 *   3. Mobile drawer — focus-trapped dialog, Escape closes, focus returns
 *   4. Mobile search toggle — Escape dismisses (predictive popup, then panel)
 *   5. Announcement ribbon dismiss — persisted per message
 *
 * prefers-reduced-motion is handled entirely in CSS (qg-tokens.css zeroes
 * --qg-duration-* under the media query) — nothing to gate here.
 */
(function () {
  'use strict';

  var THEME_STORAGE_KEY = 'qg-theme';
  var ROOT = document.documentElement;

  /* ----------------------------------------------------------------
     1. Theme toggle
  ---------------------------------------------------------------- */
  function getStoredTheme() {
    try {
      return window.localStorage.getItem(THEME_STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  function setStoredTheme(value) {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, value);
    } catch (e) {
      /* localStorage unavailable (private mode / disabled) — theme still
         applies for this page view via the data attribute, just doesn't persist. */
    }
  }

  function effectiveTheme() {
    var explicit = ROOT.getAttribute('data-qg-theme');
    if (explicit === 'light' || explicit === 'dark') return explicit;
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  function applyTheme(theme) {
    ROOT.setAttribute('data-qg-theme', theme);
    document.querySelectorAll('[data-qg-theme-toggle]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', theme === 'dark' ? 'true' : 'false');
    });
    /* QC condition C6 (QCD-D06). The pre-paint script in layout/theme.liquid sets
       <meta name="theme-color"> for the theme resolved AT LOAD, but nothing updated
       it when the visitor pressed the toggle — so the browser UI kept the old
       theme's colour until the next navigation. Same value pair as the head script;
       kept in sync deliberately. */
    var meta = document.getElementById('qg-theme-color');
    if (meta) meta.setAttribute('content', theme === 'dark' ? '#0D1424' : '#F7F6F3');
  }

  function initTheme() {
    var stored = getStoredTheme();
    if (stored === 'light' || stored === 'dark') {
      applyTheme(stored);
    } else {
      // No explicit choice yet: reflect the current effective theme in
      // aria-pressed without writing an explicit attribute, so the CSS
      // prefers-color-scheme fallback keeps tracking OS changes live.
      document.querySelectorAll('[data-qg-theme-toggle]').forEach(function (btn) {
        btn.setAttribute('aria-pressed', effectiveTheme() === 'dark' ? 'true' : 'false');
      });
    }
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-qg-theme-toggle]');
    if (!toggle) return;
    var next = effectiveTheme() === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    setStoredTheme(next);
  });

  // Live-track OS theme changes only while the visitor hasn't made an explicit choice.
  if (window.matchMedia) {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', function () {
      if (!getStoredTheme()) initTheme();
    });
  }

  initTheme();

  /* ----------------------------------------------------------------
     2. Mega menu — hover-open + click/keyboard operable
  ---------------------------------------------------------------- */
  var openTrigger = null;
  var openedViaHover = false;
  var closeTimer = null;

  /* RS1010-NAV-D03 / RS1010-AXE-D06 (FIX-1, rs-2026-10-10). The RS0930-NAV-D01
     scroll-padding-top (qg-sections.css) shrinks the region every focus()
     scroll-into-view aims for. Controls in the pinned header always sit inside
     that band, so a plain focus() scrolled the document up by the header
     offset (1500 -> 1120 -> 740 at 375 on drawer close) while the header itself
     never moved. Every focus move this file makes lands on an element that is
     already on screen (pinned header, its fixed drawer or its mega panel), so
     none of them needs to scroll. Keep the scroll-padding; never scroll here. */
  function focusNoScroll(el) {
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
  }

  function panelFor(trigger) {
    var id = trigger.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function closeMega(trigger, opts) {
    if (!trigger) return;
    var panel = panelFor(trigger);
    trigger.setAttribute('aria-expanded', 'false');
    if (panel) panel.hidden = true;
    if (openTrigger === trigger) {
      openTrigger = null;
      openedViaHover = false;
    }
    if (opts && opts.focusTrigger) focusNoScroll(trigger);
  }

  function openMega(trigger, opts) {
    if (openTrigger && openTrigger !== trigger) closeMega(openTrigger);
    var panel = panelFor(trigger);
    if (!panel) return;
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    openTrigger = trigger;
    openedViaHover = !!(opts && opts.viaHover);
    if (opts && opts.focusFirst) {
      /* RS0930-NAV-D02: the flyout feature image is now wrapped in a pointer-only
         link (tabindex="-1" aria-hidden="true"); never send keyboard focus there. */
      var firstLink = panel.querySelector('a:not([tabindex="-1"])');
      if (firstLink) focusNoScroll(firstLink);
    }
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-qg-mega-trigger]');
    if (trigger) {
      var isOpen = trigger.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        /* RS0805-FIX-D16 fix (2026-08-07). The panel is bound open on `mouseenter`
           AND toggled on `click`, with nothing reconciling the two: a pointer user
           who hovers the trigger (panel opens) and then clicks it (habitually, or to
           follow the link) immediately closed the panel they just opened. When the
           open state came from hover rather than an explicit click/keyboard toggle,
           this first click claims/confirms the already-open panel instead of closing
           it; openedViaHover is cleared so a SECOND click (or Escape, or moving focus
           out) closes it normally. Keyboard activation (Enter/Space dispatches
           `click` on a real <button>) is unaffected: openMega is only called with
           `viaHover: true` from the mouseenter listener below, never from the
           keyboard/ArrowDown path, so a keyboard-opened panel still toggles closed on
           the very next Enter/Space as before. */
        if (trigger === openTrigger && openedViaHover) {
          openedViaHover = false;
          return;
        }
        closeMega(trigger);
      } else {
        openMega(trigger, { focusFirst: false });
      }
      return;
    }
    // Click outside any open mega panel/trigger closes it.
    if (openTrigger && !event.target.closest('[data-qg-mega-panel]') && !event.target.closest('[data-qg-mega-trigger]')) {
      closeMega(openTrigger);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    if (openTrigger) closeMega(openTrigger, { focusTrigger: true });
  });

  // Keyboard activation (Enter/Space via click event already covers buttons);
  // ArrowDown from the trigger opens the panel and moves focus inside.
  document.addEventListener('keydown', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-qg-mega-trigger]');
    if (!trigger) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openMega(trigger, { focusFirst: true });
    }
  });

  // Hover-open on pointer devices only (touch/keyboard rely on click + Escape above).
  document.querySelectorAll('.qg-header__nav-item').forEach(function (item) {
    var trigger = item.querySelector('[data-qg-mega-trigger]');
    if (!trigger) return;
    item.addEventListener('mouseenter', function () {
      if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      clearTimeout(closeTimer);
      openMega(trigger, { focusFirst: false, viaHover: true });
    });
    item.addEventListener('mouseleave', function () {
      if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      clearTimeout(closeTimer);
      closeTimer = setTimeout(function () {
        closeMega(trigger);
      }, 150);
    });
  });

  // D-F-002 fix: close when focus leaves the OPEN PANEL/its trigger, not just
  // when it leaves the whole header. The previous check only compared against
  // `[data-qg-header]`, so tabbing from the panel's last link to the NEXT nav
  // item (still inside the header) never closed it — the panel stayed open
  // (aria-expanded=true) behind the next lane. Escape/click-outside above are
  // unaffected.
  document.addEventListener(
    'focusout',
    function (event) {
      if (!openTrigger) return;
      var panel = panelFor(openTrigger);
      var related = event.relatedTarget;
      var staysInside =
        !!related && ((panel && panel.contains(related)) || openTrigger.contains(related));
      if (!staysInside) {
        closeMega(openTrigger);
      }
    },
    true
  );

  /* ----------------------------------------------------------------
     3. Mobile drawer
  ---------------------------------------------------------------- */
  var drawerTrigger = null;

  // B3-D023 — trapFocus() (global.js) only intercepts literal Tab-key
  // keydown events; it does not remove the rest of the page from the
  // accessibility tree. `.qg-header__drawer-panel` already carries
  // role="dialog" + aria-modal="true", but aria-modal is a hint some
  // assistive-tech browse/virtual-cursor modes do not fully honor, so
  // background content (<main>, the footer, the header's own non-drawer
  // controls) stayed reachable to AT while the drawer sat visually on top —
  // confirmed live: neither <main> nor <body> carried `inert` or
  // aria-hidden with the drawer open. No `inert`-toggle pattern existed
  // anywhere else in this theme to mirror (checked: assets/*.js), so this
  // establishes one, scoped to exactly the drawer's own two ancestor
  // levels — every OTHER body-level section, and every OTHER child of the
  // header itself (logo, nav, utility icons, the hamburger trigger) — so
  // the drawer subtree is the only reachable content while open. `inert`
  // is preferred per dispatch over a manual aria-hidden sweep: it also
  // removes the subtree from the Tab order and from hit-testing in one
  // step, in every browser this theme supports.
  function setBackgroundInert(drawer, isInert) {
    var header = drawer.closest('[data-qg-header]');
    if (!header) return;
    Array.prototype.forEach.call(document.body.children, function (node) {
      if (node === header || node.contains(header)) return;
      node.inert = isInert;
    });
    Array.prototype.forEach.call(header.children, function (node) {
      if (node === drawer || node.contains(drawer)) return;
      node.inert = isInert;
    });
  }

  // PERF 2026-09-24 (static-audit.md A5). The drawer's nav tree now ships
  // inside `<template data-qg-drawer-template>` (see
  // snippets/qg-header-drawer.liquid) instead of being server-rendered into
  // the live DOM on every page load. Clone it into `.qg-header__drawer-panel`
  // the first time the drawer opens; a flag on the drawer element skips the
  // clone on every later open. `initTheme()` (§1 above) is re-run afterward
  // because the drawer's own theme-toggle button did not exist yet when the
  // page-load call ran, so its aria-pressed/icon would otherwise start wrong
  // until the visitor pressed it — this keeps RG22-S1-001/002 parity on the
  // very first open.
  function ensureDrawerContent(drawer) {
    if (drawer.getAttribute('data-qg-drawer-hydrated') === 'true') return;
    var template = drawer.querySelector('template[data-qg-drawer-template]');
    var panel = drawer.querySelector('.qg-header__drawer-panel');
    if (template && panel && 'content' in template) {
      panel.appendChild(template.content.cloneNode(true));
      template.remove();
    }
    drawer.setAttribute('data-qg-drawer-hydrated', 'true');
    initTheme();
  }

  function openDrawer(drawer, trigger) {
    ensureDrawerContent(drawer);
    drawer.hidden = false;
    document.body.classList.add('overflow-hidden');
    trigger.setAttribute('aria-expanded', 'true');
    drawerTrigger = trigger;
    setBackgroundInert(drawer, true);
    var panel = drawer.querySelector('.qg-header__drawer-panel');
    if (panel) {
      /* RS1010-NAV-D03: focus the first control ourselves, without scrolling,
         then install the trap handlers only (`null` skips trapFocus's own
         focus and its 300 ms scrolling fallback). trapFocus arms its Tab
         handler at once when focus is already inside the container
         (global.js) - the same pattern qg-disclosure.js uses. */
      var firstControl = panel.querySelector('a, button, input');
      focusNoScroll(firstControl);
      if (window.trapFocus) window.trapFocus(panel, null);
    }
  }

  function closeDrawer(drawer, opts) {
    drawer.hidden = true;
    document.body.classList.remove('overflow-hidden');
    setBackgroundInert(drawer, false);
    if (drawerTrigger) drawerTrigger.setAttribute('aria-expanded', 'false');
    /* RS1010-NAV-D03: removeTrapFocus(trigger) would call a plain focus() on
       the pinned trigger and scroll the page; remove the handlers only and
       return focus without scrolling. */
    if (window.removeTrapFocus) window.removeTrapFocus(null);
    if (!(opts && opts.keepFocus)) focusNoScroll(drawerTrigger);
    drawerTrigger = null;
  }

  /* RS1010-QA-D02 (FIX-1 follow-up). A drawer link that resolves to the
     CURRENT page plus a fragment (e.g. /pages/pro-av#build while on
     /pages/pro-av, or a bare #anchor) is a same-document jump: the browser
     scrolls behind the still-open modal drawer, so the visitor never saw
     where they went. Close the drawer synchronously in the click handler -
     before the link's default action runs - so overflow-hidden and the
     background `inert` are already gone when the browser performs the jump.
     The navigation itself is left to the browser (no preventDefault, no hash
     write: builders own `#build=` state - FIX-5). Focus then follows the
     jump to the fragment target, without scrolling; if the fragment names no
     element, focus returns to the menu button. Cross-page drawer links are
     not matched and behave exactly as before. */
  function samePageFragmentTarget(link) {
    var url;
    try {
      url = new URL(link.getAttribute('href'), window.location.href);
    } catch (e) {
      return null;
    }
    if (!url.hash || url.hash.length < 2) return null;
    var here = window.location;
    var strip = function (p) { return p.replace(/\/+$/, '') || '/'; };
    if (url.origin !== here.origin || strip(url.pathname) !== strip(here.pathname) || url.search !== here.search) return null;
    return url.hash.slice(1);
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('[data-qg-drawer] a[href]');
    if (!link) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.target && link.target !== '_self') return;
    var drawerEl = link.closest('[data-qg-drawer]');
    if (!drawerEl || drawerEl.hidden) return;
    var fragment = samePageFragmentTarget(link);
    if (fragment === null) return;
    var trigger = drawerTrigger;
    closeDrawer(drawerEl, { keepFocus: true });
    setTimeout(function () {
      // Leave focus alone if something on the page (e.g. the global anchor
      // handler or a builder) already moved it somewhere visible.
      var active = document.activeElement;
      if (active && active !== document.body && !drawerEl.contains(active)) return;
      var id = fragment;
      try { id = decodeURIComponent(fragment); } catch (e) { /* keep raw */ }
      var target = document.getElementById(id);
      if (target) {
        if (!target.matches('a[href], button, input, select, textarea, summary, [tabindex]')) {
          target.setAttribute('tabindex', '-1');
        }
        focusNoScroll(target);
      } else {
        focusNoScroll(trigger);
      }
    }, 0);
  });

  document.addEventListener('click', function (event) {
    var openBtn = event.target.closest('[data-qg-drawer-open]');
    if (openBtn) {
      var header = openBtn.closest('[data-qg-header]');
      var drawer = header && header.querySelector('[data-qg-drawer]');
      if (drawer) openDrawer(drawer, openBtn);
      return;
    }
    var closeBtn = event.target.closest('[data-qg-drawer-close]');
    if (closeBtn) {
      var drawerEl = closeBtn.closest('[data-qg-drawer]');
      if (drawerEl) closeDrawer(drawerEl);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    var openDrawerEl = document.querySelector('[data-qg-drawer]:not([hidden])');
    if (openDrawerEl) closeDrawer(openDrawerEl);
  });

  /* ----------------------------------------------------------------
     4. Mobile search toggle
  ---------------------------------------------------------------- */
  function searchToggleFor(search) {
    // The toggle owns the panel via aria-controls, but resolving it through
    // the shared header ancestor avoids having to escape the section id
    // (which contains `--` and `__`) into an attribute selector.
    var header = search.closest('[data-qg-header]');
    return header ? header.querySelector('[data-qg-search-toggle]') : null;
  }

  function setSearchOpen(search, toggle, open, opts) {
    search.classList.toggle('is-open', open);
    if (toggle) toggle.setAttribute('aria-expanded', open.toString());
    if (open) {
      var input = search.querySelector('input[type="search"]');
      if (input) focusNoScroll(input);
    } else if (opts && opts.focusToggle && toggle) {
      focusNoScroll(toggle);
    }
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-qg-search-toggle]');
    if (!toggle) return;
    var id = toggle.getAttribute('aria-controls');
    var search = id ? document.getElementById(id) : null;
    if (!search) return;
    setSearchOpen(search, toggle, !search.classList.contains('is-open'));
  });

  /* W5-A11Y-D05 fix (2026-07-31). The collapsed mobile search panel is a
     keyboard-operable disclosure that had no dismiss key: Escape was handled
     for the mega menu (§2) and the drawer (§3) but never for this panel, so a
     keyboard user who opened it below 1024px could only close it by finding
     the toggle again. Behaviour follows the WAI-ARIA combobox pattern, in two
     steps, because the input inside the panel IS a combobox when predictive
     search is on:
       1. Escape while the predictive popup is open dismisses the popup and
          keeps focus in the input (vendor predictive-search.js binds keyup for
          ArrowUp/ArrowDown/Enter only — it has no Escape branch at all).
       2. Escape with no popup open closes the panel and returns focus to the
          toggle that opened it.
     The drawer is a modal dialog and outranks this panel, so when the drawer
     is open Escape is left to §3 above. */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    var search = document.querySelector('[data-qg-search].is-open');
    if (!search) return;
    if (document.querySelector('[data-qg-drawer]:not([hidden])')) return;

    var predictive = search.querySelector('predictive-search[open]');
    if (predictive && typeof predictive.close === 'function') {
      predictive.close();
      return;
    }
    setSearchOpen(search, searchToggleFor(search), false, { focusToggle: true });
  });

  /* ----------------------------------------------------------------
     5. Announcement ribbon dismiss (persisted per message)
  ---------------------------------------------------------------- */
  document.addEventListener('click', function (event) {
    var dismiss = event.target.closest('[data-qg-announcement-dismiss]');
    if (!dismiss) return;
    var ribbon = dismiss.closest('[data-qg-announcement]');
    if (!ribbon) return;
    var key = ribbon.getAttribute('data-qg-announcement-key');
    if (key) {
      try {
        window.localStorage.setItem(key, '1');
      } catch (e) {
        /* no-op: dismiss still applies for this page view */
      }
    }
    ribbon.hidden = true;
  });

  /* ----------------------------------------------------------------
     6. Sticky-header offset (RS0930-NAV-D01 / RS0930-AXE-D04, RC-01)
     Nothing published the pinned header's height, so in-page anchor jumps
     and Tab-driven focus scrolls landed under the sticky header. Publish
     --qg-header-offset on :root from the box that is actually pinned at
     top:0 — `.section-qg-header` (qg-sections.css, D-F-001; measured equal
     to .qg-header__chrome: 170px at 1440, 128px at 375). Consumers:
     qg-sections.css `html:has(.qg-header--sticky) { scroll-padding-top }`
     and global.js's smooth-anchor handler. Deliberately NOT
     --header-height-live (that would revive 6 dormant vendor consumers).
  ---------------------------------------------------------------- */
  (function () {
    if (!document.querySelector('.qg-header--sticky')) return;
    var pinned = document.querySelector('.section-qg-header');
    if (!pinned) return;
    function publishOffset() {
      ROOT.style.setProperty('--qg-header-offset', Math.ceil(pinned.getBoundingClientRect().height) + 'px');
    }
    publishOffset();
    if ('ResizeObserver' in window) {
      new ResizeObserver(publishOffset).observe(pinned);
    } else {
      window.addEventListener('resize', publishOffset);
    }
  })();
})();
